import React, { forwardRef, useCallback, useEffect, useImperativeHandle, useState } from 'react';
import { Box } from '@mui/material';
import ResourceAPI from 'api/ResourceAPI';
import UserApiService from 'api/UserApiService';
import { PropertyNameOptions, SelectTypeOptions } from 'components/Select';
import { useTranslation } from 'react-i18next';
import useApi from 'hooks/useApi';
import { useSelector } from 'store/hooks';
import { useAppState } from 'store/Provider';
import moment, { Moment } from 'moment';
import Order from 'types/entities/Order';
import User from 'types/entities/User';
import Scope from 'types/entities/Scope';
import TMOrderScope from 'types/entities/TMOrderScope';
import { updateIds } from 'helpers/orderhelper';
import { OrderType } from 'types/enums/OrderType';
import ScopeApiService from 'api/ScopeApiService';
import EngagementParaOrder from './EngagementParaOrder';
import affairApiService from 'api/AffairApiService';
import { loadAffairsAction } from 'store/actions/appActions';
import { getPurchaseOrderAction } from 'store/actions/purchaseOrderActions';
import { ActionDescriptor } from 'types/enums/ActionDescriptor';
import { setSnackbarAction } from 'store/actions/snackbarActions';
import { loadCustomersAction, getbaseLineEditOrderAction } from 'store/actions/changeBaselineActions';
import { openDialogAction } from 'store/actions/dialogsActions';
import OrderActionRestrictionDialog from 'features/Orders/OrderDialogs/OrderActionRestrictionDialog';
import { addLoadingAction, removeLoadingAction } from 'store/actions/loadingsActions';
import orderApiService from 'api/OrderApiService';
import { formatDateToddMMYYY } from 'helpers/format';
import { injectOrderAction } from 'store/actions/catalogToOrderActions';
import { editManagementOrderAction } from 'store/actions/managementActions';

export interface EngagementParametersRefType {
  handleSubmitEngagementPara: () => void;
}

interface EngagementParametersProps {
  selectedConsultants: any;
  setSelectedConsultants: React.Dispatch<React.SetStateAction<any>>;
  deliveryManagers: any;
  setDeliveryManagers: React.Dispatch<React.SetStateAction<any>>;
  selectedDeliveryManager: any;
  setSelectedDeliveryManager: React.Dispatch<React.SetStateAction<any>>;
  selectedScopes: Scope[];
  setSelectedScopes: React.Dispatch<React.SetStateAction<any>>;
  representatives: any;
  setRepresentatives: React.Dispatch<React.SetStateAction<any>>;
  selectedRepresentative: any;
  setSelectedRepresentative: React.Dispatch<React.SetStateAction<any>>;
  setIsCreating: React.Dispatch<React.SetStateAction<any>>;
  isCreating: boolean;
  currentStep: number;
  setActiveBaseFlag: React.Dispatch<React.SetStateAction<any>>;
  handleNext: () => void;
  handleCancel: React.Dispatch<React.SetStateAction<any>>;
  onOrderCreated?: (order: Order) => void;
  savedFetchOrder?: (fnName: boolean, baseline_version: number) => void;
  availableScopes?: Scope[];
  availableDeliveryManagers?: User[];
  availableConsultants?: User[];
  customerId: User['id'];
  parent_catalog_id: string;
  sourceBaseOrder: Partial<Order> | null;
}

const EngagementParameters = forwardRef<EngagementParametersRefType, EngagementParametersProps>((props, ref) => {
  const {
    selectedConsultants,
    setSelectedConsultants,
    deliveryManagers,
    setDeliveryManagers,
    selectedDeliveryManager,
    setSelectedDeliveryManager,
    selectedScopes,
    setSelectedScopes,
    selectedRepresentative,
    setSelectedRepresentative,
    isCreating,
    setIsCreating,
    currentStep,
    setActiveBaseFlag,
    handleNext,
    handleCancel,
    onOrderCreated,
    savedFetchOrder,
    availableScopes,
    availableDeliveryManagers,
    availableConsultants,
    customerId,
    parent_catalog_id,
    sourceBaseOrder,
  } = props;

  const [t] = useTranslation();
  const { dispatch } = useAppState();
  const appState = useSelector((state) => state.app);
  const dashboard = useSelector((state) => state.dashboard);

  const dialogData = useSelector((state) => state.dialogs);
  const purchaseOrder = useSelector((state) => state.purchaseOrder.datas);
  const [editOrder, setEditOrder] = useState<Partial<Order> | undefined>();
  const { makeCall } = useApi();

  const initialDialogData = {
    data: {
      order: sourceBaseOrder != null ? sourceBaseOrder : [],
    },
  };
  const restrictionDialogMessage = dialogData?.data?.message || '';

  // State variables
  const [initialDiaData, setInitialDiaData] = useState(initialDialogData);
  // const [creators, setCreators] = useState<User[]>([]);
  const [scopes, setScopes] = useState<Scope[]>([]);
  const [archivedScopes, setArchivedScopes] = useState<Scope[]>([]);
  const [clients, setClients] = useState<User[]>([]);
  const [consultants, setConsultants] = useState<User[]>([]);
  const [archivedConsultants, setArchivedConsultants] = useState<User[]>([]);
  const [archivedDeliveryManagers, setArchivedDeliveryManagers] = useState<User[]>([]);
  const [archivedRepresentatives, setArchivedRepresentatives] = useState<User[]>([]);
  const [representatives, setRepresentatives] = useState<User[]>([]);
  const [purchaseOrderOTP, setPurchaseOrderOTP] = useState();
  // Tracks whether the user has actually edited anything since the order was loaded,
  // so Save can skip the API call and just navigate when nothing changed.
  const hasChangesRef = React.useRef(false);
  // Set edit order when order changes
  useEffect(() => {
    if (sourceBaseOrder !== null) {
      setEditOrder(sourceBaseOrder ?? undefined);
      hasChangesRef.current = false;
      // dispatch(getbaseLineEditOrderAction(sourceOrder));
    }
    // else {
    //   setEditOrder(baseLineOrder);
    //   dispatch(getbaseLineEditOrderAction(baseLineOrder));
    // }
  }, [sourceBaseOrder]);

  // Fetch affairs directly if not yet loaded (e.g. entering the catalog-order wizard for a
  // customer whose affairs App.tsx never loaded). Without this the Engagement-by-name/OTP
  // dropdowns render empty. Mirrors the fallback in TnMCreationOrder.
  useEffect(() => {
    if (!appState.affairs?.length && customerId) {
      const fetchAffairs = async () => {
        const fetched = await affairApiService.fetchByCustomerId(customerId as number);
        if (fetched?.length) dispatch(loadAffairsAction(fetched));
      };
      fetchAffairs();
    }
  }, [customerId]);

  // Navigating here from the ToDo dashboard's "Create order" dialog stores the picked
  // engagement in state.dashboard.todoOrderCreateList (dispatched by todoCreateOrderScreen).
  // Pre-fill the affair fields from it, same match-by-otp/name logic as CreationOrder.tsx and
  // TnMCreationOrder.tsx. Skipped once editing a real order so it can't clobber its own affair.
  useEffect(() => {
    if (dashboard.todoOrderCreateList) {
      const { affair_id, otp } = dashboard.todoOrderCreateList;
      const matchedAffair =
        appState.affairs.find((a) => String(a.otp) === String(otp)) ??
        appState.affairs.find((a) => a.id === affair_id);
      if (!matchedAffair) return;

      hasChangesRef.current = true;
      setPurchaseOrderOTP((matchedAffair as any)?.otp);
      setEditOrder((prev) => ({ ...prev, affair: matchedAffair, affair_id: matchedAffair.id }));
    }

  }, [dashboard.todoOrderCreateList, appState.affairs, sourceBaseOrder]);


  // Initialize data on mount
  useEffect(() => {
    const initializeData = async () => {
      try {
        // Get scopes
        if (customerId) {
          const scopesRetrieved = await ScopeApiService.fetchByCustomerId(customerId);
          setScopes(scopesRetrieved);
        }
        // Get creators
        const creatorsFetched = await UserApiService.fetchPossiblesDelegues();
        setRepresentatives(creatorsFetched);

        // Reset purchase orders
        // dispatch(getPurchaseOrderAction([]));
      } catch (error) {
        console.error('Error initializing data:', error);
      }
    };

    initializeData();
  }, [customerId, representatives.length]);

  useEffect(() => {
    const getPurchaseOrder = async () => {
      if (purchaseOrderOTP) {
        dispatch(addLoadingAction('purchaseorderlist'));
        const purchaseOrderList = await orderApiService.fetchPurchaseOrder(purchaseOrderOTP);
        purchaseOrderList.forEach(
          (po: {
            poend: string;
            poref: string;
            reference: string;
            postart: string;
            is_valid: boolean;
            is_archived: boolean;
          }) => {
            po.is_valid = false; // Set is_valid to false for all items
            po.is_archived = true; // Purchase orders default to inactive until manually activated
            po.reference = `${po.poref} (${formatDateToddMMYYY(po.postart)} - ${formatDateToddMMYYY(po.poend)})`;
          }
        );
        dispatch(getPurchaseOrderAction(purchaseOrderList));
        dispatch(removeLoadingAction('purchaseorderlist'));
      }
    };
    getPurchaseOrder();
  }, [purchaseOrderOTP]);

  // Fetch clients - Fixed implementation
  useEffect(() => {
    if (!customerId || clients.length > 0) return;
    const getClients = async () => {
      try {
        const clientsData = await makeCall(
          UserApiService.getClientsOnCustomer(customerId, {
            is_archived: false,
            size: '-1',
          }),
          'Failed to retrieve clients'
        );
        if (clientsData) {
          dispatch(loadCustomersAction(clientsData?.datas));
        }
        setClients(clientsData?.datas || []);
      } catch (error) {
        console.error('Error fetching clients:', error);
        setClients([]);
      }
    };
    getClients();
  }, [customerId, clients.length]);

  // Fetch consultants
  useEffect(() => {
    if (!customerId || consultants.length > 0) return;
    const getConsultants = async () => {
      try {
        const consultantsData = await makeCall(
          UserApiService.fetchPossiblesConsultants(),
          'Unable to retrieve consultants'
        );
        setConsultants(consultantsData || []);
      } catch (error) {
        console.error('Error fetching consultants:', error);
        setConsultants([]);
      }
    };
    getConsultants();
  }, [customerId, consultants.length]);

  // Fetch delivery managers
  useEffect(() => {
    if (!customerId || deliveryManagers.length > 0) return;
    const getDeliveryManagers = async () => {
      try {
        const deliveryManagersData = await makeCall(
          UserApiService.fetchPossiblesDeliverableTeamLeaders(),
          'Unable to retrieve delivery managers'
        );
        setDeliveryManagers(deliveryManagersData || []);
      } catch (error) {
        console.error('Error fetching delivery managers:', error);
        setDeliveryManagers([]);
      }
    };
    getDeliveryManagers();
  }, [customerId, deliveryManagers.length]);

  // Form handlers
  const handleChange = (e: React.FormEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    e.preventDefault();
    hasChangesRef.current = true;
    if (e.currentTarget.name === 'draft_reference' && e.currentTarget.value === '') {
      return setEditOrder({ ...editOrder, [e.currentTarget.name]: null });
    }
    dispatch(getbaseLineEditOrderAction({ ...editOrder, [e.currentTarget.name]: e.currentTarget.value }));
    dispatch(injectOrderAction({ ...editOrder, [e.currentTarget.name]: e.currentTarget.value } as Order));
    return setEditOrder({ ...editOrder, [e.currentTarget.name]: e.currentTarget.value });
  };

  const handleSelectClient = (client: User | null | undefined) => {
    hasChangesRef.current = true;
    if (client) {
      dispatch(getbaseLineEditOrderAction({ ...editOrder, client, client_id: client.id }));
      dispatch(injectOrderAction({ ...editOrder, client, client_id: client.id } as Order));
      return setEditOrder({ ...editOrder, client, client_id: client.id });
    }
    return setEditOrder({ ...editOrder, client: undefined, client_id: undefined });
  };

  // const handleSelectRepresentative = (representative: User | null | undefined) => {
  //   if (representative) return setEditOrder({ ...editOrder, delegue: representative, delegue_id: representative.id });
  //   return setEditOrder({ ...editOrder, delegue: undefined, delegue_id: undefined });
  // };

  const handleSelectOptions = (e: SelectTypeOptions | null, property: PropertyNameOptions) => {
    hasChangesRef.current = true;
    if (e && property === 'affair') {
      setPurchaseOrderOTP((e as any)?.otp);
      return setEditOrder({ ...editOrder, [`${property}`]: e, [`${property}_id`]: e.id });
    }
    return setEditOrder({ ...editOrder, affair: undefined, affair_id: undefined });
  };

  const handleDateChange = (name: string, date: Moment | null) => {
    hasChangesRef.current = true;
    const formattedDate = date?.format('YYYY-MM-DD');
    dispatch(getbaseLineEditOrderAction({ ...editOrder, [name]: formattedDate }));
    dispatch(injectOrderAction({ ...editOrder, [name]: formattedDate } as Order));
    setEditOrder({ ...editOrder, [name]: formattedDate });
  };

  // Expose child function to parent
  useImperativeHandle(ref, () => ({
    handleSubmitEngagementPara,
  }));

  const handleSubmitEngagementPara = useCallback(async () => {
    if (isCreating || !editOrder) {
      console.log('Creation already in progress or no order data');
      return;
    }

    // Editing an existing order with no changes: just move on, don't re-save.
    if (sourceBaseOrder?.id && !hasChangesRef.current) {
      handleNext();
      return;
    }

    setIsCreating(true);
    const { affair, client, production_at, catalog_id, status, ...orderToSave } = editOrder;

    const { ids: accountable_ids, archived: is_accountable_archived } = updateIds(
      selectedDeliveryManager,
      archivedDeliveryManagers
    );

    const { ids: consultant_ids, archived: is_consultant_archived } = updateIds(
      selectedConsultants,
      archivedConsultants
    );

    const { ids: representative_ids, archived: is_representative_archived } = updateIds(
      selectedRepresentative,
      archivedRepresentatives
    );

    const { ids: scope_ids, archived: is_scope_archived } = updateIds(selectedScopes, archivedScopes);

    const client_ids = client?.id ? [client?.id] : null;
    const is_client_archived = client_ids !== null ? [false] : [];

    const tmOrderScope: TMOrderScope = {
      id: 0, // This will be generated by the backend
      accountable_ids,
      consultant_ids,
      delegue_ids: representative_ids,
      client_ids,
      scope_ids,
      order_id: orderToSave.id as number,
      is_scope_archived,
      is_accountable_archived,
      is_consultant_archived,
      is_client_archived,
      is_delegue_archived: is_representative_archived,
    };

    try {
      const orderPayload = {
        ...orderToSave,
        client_id: editOrder?.client_id,
        catalog_id: parent_catalog_id ?? editOrder?.catalog_id,
        customer_id: appState.customer?.id,
        affair_name: affair?.name || null,
        affair_otp: affair?.otp || null,
        affair_id: affair?.id || null,
        creator_id: appState.user?.id,
        order_type_id: OrderType.CATALOG,
        tm_order_scope: tmOrderScope,
        purchase_orders: purchaseOrder,
      };

      const res = editOrder?.id
        ? await ResourceAPI.patchById('orders', editOrder.id, {
            ...orderPayload,
            action_id: ActionDescriptor.UpdateCatalogOrder,
          })
        : await ResourceAPI.post('orders', {
            ...orderPayload,
            status,
            action_id: ActionDescriptor.CreateCatalogOrder,
          });
      if (res) {
        dispatch(
          editManagementOrderAction({
            ...editOrder,
            client_id: res.data.order.client_id,
            affair_id: res.data.order.affair_id,
          } as Order)
        );
        onOrderCreated?.(res.data.order);
      }
      dispatch(getPurchaseOrderAction([]));
    } catch (error) {
      const result = (error as Error).message;

      if (result) {
        dispatch(
          setSnackbarAction({
            message: `Fetch order-workunits from active catalog failed : ${result}`,
            open: true,
            severity: 'error',
          })
        );
      } else {
        dispatch(
          setSnackbarAction({
            message: `Fetch order-workunits from active catalog failed : ${error}`,
            open: true,
            severity: 'error',
          })
        );
      }
    } finally {
      setIsCreating(false);
      dispatch(getPurchaseOrderAction([]));
    }
  }, [
    isCreating,
    editOrder,
    setIsCreating,
    sourceBaseOrder,
    handleNext,
    selectedDeliveryManager,
    archivedDeliveryManagers,
    selectedConsultants,
    archivedConsultants,
    selectedRepresentative,
    archivedRepresentatives,
    selectedScopes,
    archivedScopes,
    parent_catalog_id,
    appState,
    purchaseOrder,
    dispatch,
    onOrderCreated,
  ]);

  const handleSwitchSign = (value: boolean) => {
    hasChangesRef.current = true;
    setEditOrder({ ...editOrder, is_sign_mandatory: value });
  };

  const handleClientConnectionSwitch = (value: boolean) => {
    hasChangesRef.current = true;
    setEditOrder({ ...editOrder, client_connection: value });
  };

  const handleScopeAdded = (newScope: Scope) => {
    const scopeWithArchiveStatus = { ...newScope, is_archived: false };
    setScopes((prevScopes) => [...prevScopes, scopeWithArchiveStatus]);
  };

  const handleScopeArchived = (updatedScope: Scope) => {
    setScopes((prevScopes) =>
      prevScopes.map((scope) => (scope.id === updatedScope.id ? { ...scope, is_archived: true } : scope))
    );
    const deliverable_scope = (initialDialogData?.data.order as Order | undefined)?.scopes;
    const existsById = deliverable_scope?.some((item) => item.id === updatedScope.id);
    if (!existsById) {
      setArchivedScopes((prevArchivedScopes) => [...prevArchivedScopes, updatedScope]);
    }
  };

  const handleScopesChange = React.useCallback(
    (newSelectedScopes: Scope[]) => {
      hasChangesRef.current = true;
      setSelectedScopes(newSelectedScopes);
    },
    [setSelectedScopes]
  );

  const handleConsultantChange = (newSelectedConsultants: User[], reason?: string) => {
    hasChangesRef.current = true;
    setSelectedConsultants(newSelectedConsultants);
  };

  const handleConsultantArchived = (updatedUser: User) => {
    setArchivedConsultants((prevArchiveConsultant) => [...prevArchiveConsultant, updatedUser]);
  };

  const handleDeliveryManagerChange = (newSelectedDeliveryManager: User[], reason?: string) => {
    hasChangesRef.current = true;
    setSelectedDeliveryManager(newSelectedDeliveryManager);
  };

  const handleDeliveryManagerArchived = (updatedUser: User) => {
    setArchivedDeliveryManagers((prevArchivedDeliveryManager) => [...prevArchivedDeliveryManager, updatedUser]);
  };

  const handleRepresentativeChange = (newSelectedRepresentative: User[], reason?: string) => {
    hasChangesRef.current = true;
    setSelectedRepresentative(newSelectedRepresentative);
  };

  const handleRepresentativeArchived = (updatedUser: User) => {
    setArchivedRepresentatives((prevArchivedRepresentatives) => [...prevArchivedRepresentatives, updatedUser]);
  };

  const handleScopeDeletionMessage = useCallback(() => {
    setInitialDiaData((prev) => ({
      ...prev,
      data: {
        order: prev.data?.order,
      },
    }));

    dispatch(
      openDialogAction({
        name: 'orderActionRestrictionDialog',
        data: {
          message: 'You are unable to perform this action. This scope is already associated with ongoing deliverables.',
        },
      })
    );
  }, [dispatch]);

  const handleDMUserDeletionMessage = useCallback(() => {
    dispatch(
      openDialogAction({
        name: 'orderActionRestrictionDialog',
        data: {
          message:
            'You are unable to perform this action. This delivery manager is already linked with ongoing deliverables.',
        },
      })
    );
  }, [dispatch]);

  const handleConsultantUserDeletionMessage = useCallback(() => {
    dispatch(
      openDialogAction({
        name: 'orderActionRestrictionDialog',
        data: {
          message:
            'You are unable to perform this action. This consultant is already linked with ongoing deliverables.',
        },
      })
    );
  }, [dispatch]);

  return (
    <Box sx={{ mb: 5 }}>
      <EngagementParaOrder
        handleChange={handleChange}
        handleSelectClient={handleSelectClient}
        handleSelectOptions={handleSelectOptions}
        handleDateChange={handleDateChange}
        handleSwitchSign={handleSwitchSign}
        handleClientConnectionSwitch={handleClientConnectionSwitch}
        order={editOrder}
        // creators={creators}
        clients={clients}
        scopes={scopes}
        consultants={consultants}
        deliveryManagers={deliveryManagers}
        representatives={representatives}
        onScopeAdded={handleScopeAdded}
        onScopeArchived={handleScopeArchived}
        handleScopesChange={handleScopesChange}
        onConsultantArchived={handleConsultantArchived}
        handleConsultantChange={handleConsultantChange}
        onDeliveryManagerArchived={handleDeliveryManagerArchived}
        handleDeliveryManagerChange={handleDeliveryManagerChange}
        onScopeDeletionAttempt={handleScopeDeletionMessage}
        onDMUserDeletionAttempt={handleDMUserDeletionMessage}
        onConsultantUserDeletionAttempt={handleConsultantUserDeletionMessage}
        onRepresentativeArchived={handleRepresentativeArchived}
        handleRepresentativeChange={handleRepresentativeChange}
        onRepresentativeUserDeletionAttempt={() => {
          // intentionally left blank
        }}
        selectedRepresentative={selectedRepresentative}
        selectedScopes={selectedScopes}
        selectedConsultants={selectedConsultants}
        selectedDeliveryManager={selectedDeliveryManager}
        availableScopes={availableScopes}
        availableDeliveryManagers={availableDeliveryManagers}
        availableConsultants={availableConsultants}
        variant="filled"
      />

      <OrderActionRestrictionDialog message={restrictionDialogMessage} />
    </Box>
  );
});

export default EngagementParameters;
